import React, { useState } from "react";
import { Modal } from "../common/Modal";
import { Answer, StaffMember } from "../../types";
import { Tag, Plus, X, Trophy, Sparkles, Trash2 } from "lucide-react";
import { DeleteAnswerModal } from "./DeleteAnswerModal";

interface AnswerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (answerData: Omit<Answer, "id" | "createdAt" | "updatedAt">) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  initialData?: Answer | null;
  staff: StaffMember[];
  availableServiceTags?: string[];
}

const AnswerForm: React.FC<AnswerModalProps> = ({
  onClose,
  onSave,
  onDelete,
  initialData,
  staff,
  availableServiceTags = [
    "Translation",
    "Localization",
    "Interpreting (VRI/OPI)",
    "Desktop Publishing (DTP)",
    "Transcription",
    "Quality Assurance",
    "Compliance & HIPAA",
    "Security",
  ],
}) => {
  const [title, setTitle] = useState(initialData?.title || "");
  const [body, setBody] = useState(initialData?.body || "");
  const [selectedTags, setSelectedTags] = useState<string[]>(initialData?.serviceTags || []);
  const [customTag, setCustomTag] = useState("");
  const [owner, setOwner] = useState(initialData?.owner || staff[0]?.displayName || "Katie");
  const [sourceProposal, setSourceProposal] = useState(initialData?.sourceProposal || "");
  const [intervalDays, setIntervalDays] = useState(180);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const handleAddCustomTag = (e: React.KeyboardEvent | React.MouseEvent) => {
    if ("key" in e && e.key !== "Enter") return;
    e.preventDefault();
    const trimmed = customTag.trim();
    if (trimmed && !selectedTags.includes(trimmed)) {
      setSelectedTags((prev) => [...prev, trimmed]);
      setCustomTag("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    setSelectedTags((prev) => prev.filter((t) => t !== tagToRemove));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      setError("Please provide both a title and response narrative body.");
      return;
    }

    setSubmitting(true);
    setError(null);

    const today = new Date();
    const lastVerified = initialData?.lastVerifiedDate || today.toISOString().slice(0, 10);
    const reviewDue =
      initialData?.reviewDueDate ||
      new Date(today.getTime() + intervalDays * 86400000).toISOString().slice(0, 10);

    const selectedStaffMember = staff.find((s) => s.displayName === owner);

    try {
      await onSave({
        title: title.trim(),
        body: body.trim(),
        serviceTags: selectedTags,
        owner: owner || "Unassigned",
        ownerEmail: selectedStaffMember?.email || "",
        lastVerifiedDate: lastVerified,
        reviewDueDate: reviewDue,
        status: initialData?.status || "active",
        ...(sourceProposal.trim() ? { sourceProposal: sourceProposal.trim() } : {}),
        ...(initialData?.wonBidCount !== undefined ? { wonBidCount: initialData.wonBidCount } : {}),
        ...(initialData?.wonBidIds ? { wonBidIds: initialData.wonBidIds } : {}),
        ...(initialData?.sourceOpportunityId
          ? { sourceOpportunityId: initialData.sourceOpportunityId }
          : {}),
        ...(initialData?.sourceOutcome ? { sourceOutcome: initialData.sourceOutcome } : {}),
        ...(initialData?.harvestedFrom ? { harvestedFrom: initialData.harvestedFrom } : {}),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save answer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded-lg bg-rose-50 p-3 text-xs font-medium text-rose-700 dark:bg-rose-950/50 dark:text-rose-300">
          {error}
        </div>
      )}

      {initialData && (initialData.wonBidCount || 0) > 0 && (
        <div className="flex items-center gap-2 rounded-lg bg-amber-50 p-2.5 text-xs font-medium text-amber-800 border border-amber-200 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-300">
          <Trophy className="h-4 w-4 text-amber-500 shrink-0" />
          <span>
            This answer has been credited in {initialData.wonBidCount} winning proposal
            {initialData.wonBidCount === 1 ? "" : "s"}.
          </span>
        </div>
      )}

      {initialData?.harvestedFrom && (
        <div className="flex items-center gap-2 rounded-lg bg-teal-50 p-2.5 text-xs font-medium text-teal-800 border border-teal-200 dark:bg-teal-950/40 dark:border-teal-900 dark:text-teal-300">
          <Sparkles className="h-4 w-4 text-[#2ba5b5] shrink-0" />
          <span>
            Harvested from {initialData.harvestedFrom.clientName} (
            {initialData.harvestedFrom.opportunityTitle})
          </span>
        </div>
      )}

      <div>
        <label className="block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400">
          Answer Title / Question Topic <span className="text-rose-500">*</span>
        </label>
        <input
          type="text"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Linguistic Quality Assurance Protocol"
          className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
        />
      </div>

      <div>
        <div className="flex items-center justify-between">
          <label className="block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400">
            Verified Narrative Text <span className="text-rose-500">*</span>
          </label>
          <span className="text-[11px] text-slate-400">
            {body.trim().split(/\s+/).filter(Boolean).length} words • {body.length} chars
          </span>
        </div>
        <textarea
          required
          rows={5}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Enter standard, approved narrative content ready for direct inclusion into RFP proposals..."
          className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 font-sans leading-relaxed"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400">
          Service & Concept Tags
        </label>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {availableServiceTags.map((tag) => {
            const isSelected = selectedTags.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTag(tag)}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                  isSelected
                    ? "bg-[#2ba5b5] text-white shadow-2xs"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                }`}
              >
                <Tag className="h-3 w-3" />
                <span>{tag}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-2 flex items-center gap-2">
          <input
            type="text"
            value={customTag}
            onChange={(e) => setCustomTag(e.target.value)}
            onKeyDown={handleAddCustomTag}
            placeholder="Add custom tag and press Enter..."
            className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
          />
          <button
            type="button"
            onClick={handleAddCustomTag}
            className="btn-animate inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>Add</span>
          </button>
        </div>

        {selectedTags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {selectedTags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 rounded-md bg-teal-50 px-2 py-0.5 text-xs text-[#2ba5b5] dark:bg-teal-950/60"
              >
                <span>{tag}</span>
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="hover:text-teal-700"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400">
            Answer Owner (Staff HQ)
          </label>
          <select
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
          >
            {staff.map((s) => (
              <option key={s.id} value={s.displayName}>
                {s.displayName} {s.department ? `(${s.department})` : ""}
              </option>
            ))}
            {!staff.some((s) => s.displayName === owner) && owner && (
              <option value={owner}>{owner}</option>
            )}
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400">
            Source Proposal Citation
          </label>
          <input
            type="text"
            value={sourceProposal}
            onChange={(e) => setSourceProposal(e.target.value)}
            placeholder="e.g., Cook County Health RFP 2026"
            className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
          />
        </div>
      </div>

      {!initialData && (
        <div>
          <label className="block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400">
            Verification Freshness Interval
          </label>
          <select
            value={intervalDays}
            onChange={(e) => setIntervalDays(Number(e.target.value))}
            className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
          >
            <option value={90}>90 days (Quarterly Review - Pricing/Compliance)</option>
            <option value={180}>180 days (Semi-Annual Review - Standard)</option>
            <option value={365}>365 days (Annual Review - Evergreen Narrative)</option>
          </select>
        </div>
      )}

      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 pt-4">
        <div>
          {initialData && onDelete && (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={submitting}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-100 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
              <span>Delete Answer</span>
            </button>
          )}
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="btn-animate rounded-lg bg-[#2ba5b5] px-4 py-2 text-sm font-semibold text-white hover:bg-[#238997] disabled:opacity-50"
          >
            {submitting ? "Saving..." : initialData ? "Update Answer" : "Save Answer"}
          </button>
        </div>
      </div>

      {showDeleteConfirm && initialData && onDelete && (
        <DeleteAnswerModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          answer={initialData}
          onConfirmDelete={async (id) => {
            await onDelete(id);
            setShowDeleteConfirm(false);
            onClose();
          }}
        />
      )}
    </form>
  );
};

export const AnswerModal: React.FC<AnswerModalProps> = (props) => {
  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={props.initialData ? "Edit Approved Answer" : "Add Approved Library Answer"}
      description="Record or update verified narrative responses vetted for quality, compliance, and procurement rigor."
    >
      <AnswerForm key={props.initialData?.id || "new-answer"} {...props} />
    </Modal>
  );
};
